import React from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DescriptionIcon from "@mui/icons-material/Description";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import GuideSection from "../GuideSection";
import CONSTANTS from "../../constants";

const guideItems = [
  {
    title: "Suggestion",
    summary:
      "Use an account for the best experience, saved history, and ranking display.",
    icon: TipsAndUpdatesIcon,
    tint: "#edf7ff",
    color: "#2196f3",
    keyName: "suggestion",
  },
  {
    title: "Step 1: Download Document",
    summary: "Find learning resources and download the material you need.",
    icon: DescriptionIcon,
    tint: "#e8f9ed",
    color: "#35c759",
    keyName: "stepOne",
  },
  {
    title: "Step 2: Do the Test",
    summary: "Open a TOEIC answer sheet, start the timer, and submit answers.",
    icon: AssignmentTurnedInIcon,
    tint: "#edf7ff",
    color: "#2196f3",
    keyName: "stepTwo",
  },
  {
    title: "Step 3: Other Functions",
    summary: "Review past attempts and check the leaderboard.",
    icon: EmojiEventsIcon,
    tint: "#e8f9ed",
    color: "#35c759",
    keyName: "stepThree",
  },
];

function Guide() {
  const { suggestion, stepOne, stepTwo, stepThree } = CONSTANTS;
  const guideMap = { suggestion, stepOne, stepTwo, stepThree };

  return (
    <Box sx={{ width: "100%", mt: 4, mb: 6 }}>
      <Box component="header">
        <Typography
          component="h1"
          sx={{
            color: "var(--toeic-ink-deep)",
            fontSize: { xs: 36, md: 56 },
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: { xs: 1.05, md: 1 },
          }}
        >
          <Box component="span" sx={{ position: "relative", display: "inline-block" }}>
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                position: "absolute",
                left: "-0.12em",
                right: "-0.12em",
                bottom: "0.04em",
                height: "0.44em",
                bgcolor: "rgba(70, 168, 232, 0.24)",
                borderRadius: "0.45em 0.6em 0.4em 0.55em",
                transform: "rotate(-1deg)",
              }}
            />
            <Box component="span" sx={{ position: "relative" }}>
              How To Use Lingualica
            </Box>
          </Box>
          .
        </Typography>
        <Typography sx={{ mt: 2, color: "var(--toeic-ink-soft)", maxWidth: 720, fontSize: 18, fontWeight: 700, lineHeight: 1.65 }}>
          Follow the guide to download material, take tests, review results, and
          use the leaderboard properly.
        </Typography>
      </Box>

      <Box sx={{ mt: 4, display: "grid", gap: 4 }}>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
        <Box
          className="toeic-reveal"
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: "18px",
            border: "1px solid rgba(215, 234, 245, 0.82)",
            bgcolor: "var(--toeic-paper-grid)",
            boxShadow: "var(--toeic-shadow-card)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              color: "var(--toeic-ink)",
              fontSize: 14,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".12em",
            }}
          >
            Guide Sections
          </Typography>
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 12, fontWeight: 700 }}>
            {guideItems.length} sections - step by step
          </Typography>
        </Box>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
        {guideItems.map((item, index) => {
          const Icon = item.icon;
          const steps = guideMap[item.keyName] || [];

          return (
            <Accordion
              key={item.title}
              defaultExpanded={index === 0}
              sx={{
                width: "100%",
                border: 0,
                boxShadow: "var(--toeic-shadow-card)",
                borderRadius: "14px !important",
                overflow: "hidden",
                bgcolor: "var(--toeic-surface)",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "var(--toeic-ink-soft)" }} />}
                aria-controls={`${item.keyName}-content`}
                id={`${item.keyName}-header`}
                sx={{ bgcolor: "var(--toeic-surface)", px: 2.5, py: 1 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "14px",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: item.tint,
                      color: item.color,
                      flex: "0 0 auto",
                    }}
                  >
                    <Icon />
                  </Box>
                  <Box>
                    <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 14, fontWeight: 700 }}>
                      {item.summary}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: "var(--toeic-paper)", p: { xs: 1.5, md: 2.5 } }}>
                <Box sx={{ display: "grid", gap: 2 }}>
                  {steps.map((step, stepIndex) => (
                    <GuideSection
                      key={stepIndex}
                      image={step.image}
                      content={step.content}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
}

export default Guide;
